// src/pages/help/HelpPage.tsx

import React, { useState } from "react";
import { Tabs } from "antd";
import { BellOutlined, CustomerServiceOutlined, ReadOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { PageContainer } from "../../components/shared";
import UpdateNotificationsTab from "./UpdateNotificationsTab";
import SupportTicketsTab from "./SupportTicketsTab";
import GuidesTab from "./guides/GuidesTab";
import Layout from "../../components/Layout";


/**
 * Rendered at both /help and /help/guides (App.tsx) — the Guides tab is a ROUTE so guide
 * URLs are deep-linkable, while the other two tabs stay local state. Switching from the
 * guides route back to a local tab navigates to /help carrying the chosen tab in
 * location.state, because the route change remounts this component and plain useState
 * would land the user on the default tab instead of the one they clicked.
 */
const HelpPage: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const onGuidesRoute = location.pathname.startsWith("/help/guides");
	const initialTab = (location.state as { tab?: string } | null)?.tab ?? "notifications";
	const [localTab, setLocalTab] = useState(initialTab);
	const activeKey = onGuidesRoute ? "guides" : localTab;

	const items = [
		{
			key: "notifications",
			label: (
				<span>
					<BellOutlined /> Update Notifications
				</span>
			),
			children: <UpdateNotificationsTab />,
		},
		{
			key: "support",
			label: (
				<span>
					<CustomerServiceOutlined /> Support Tickets
				</span>
			),
			children: <SupportTicketsTab />,
		},
		{
			key: "guides",
			label: (
				<span>
					<ReadOutlined /> User Guides
				</span>
			),
			children: <GuidesTab />,
		},
	];

	const onTabChange = (key: string) => {
		if (key === "guides") {
			navigate("/help/guides");
			return;
		}
		if (onGuidesRoute) {
			navigate("/help", { state: { tab: key } });
			return;
		}
		setLocalTab(key);
	};

	return (
		<Layout>
			<PageContainer
				title="Help & Support"
				subtitle="User guides, system update notifications and your support tickets"
				data-testid="help-page"
			>
				<Tabs activeKey={activeKey} onChange={onTabChange} items={items} />
			</PageContainer>
		</Layout>
	);
};

export default HelpPage;
